import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from "react";
import {
  GetCoursesByFacultyQuery,
  UnifiedSubmission,
  UpdateAssessmentSubmissionUpdatedGrade,
  UpdateStudentAssignmentGradeInput,
  useGetCoursesByFacultyQuery,
  useGetGradingCenterDashboardDataQuery,
  useGetUnifiedSubmissionsByFacultyQuery,
  useMeQuery,
} from "../graphql/generated/graphql";
import usePagination from "../hooks/usePagination";
import { useAppConfig } from "../AppConfigProvider";
import { formatSubmittedDate, formatSubmittedTime } from "../utils/date";
import { useDebounce } from "use-debounce";

export const SUBMISSION_TYPE_OPTIONS = [
  { label: "All Types", value: "All" },
  { label: "Assignment", value: "Assignment" },
  { label: "Assessment", value: "Assessment" },
  { label: "Project", value: "Project" },
];

interface GradingCenterSubmissionsContextType {
  selectedCourse: string;
  setSelectedCourse: Dispatch<SetStateAction<string>>;
  selectedModule: string;
  setSelectedModule: Dispatch<SetStateAction<string>>;
  selectedType: (typeof SUBMISSION_TYPE_OPTIONS)[number]["value"];
  setSelectedType: Dispatch<SetStateAction<(typeof SUBMISSION_TYPE_OPTIONS)[number]["value"]>>;
  showOnlyUngraded: boolean;
  setShowOnlyUngraded: Dispatch<SetStateAction<boolean>>;
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
  sortBy: string;
  selectedCourses: string[];
  setSelectedCourses: Dispatch<SetStateAction<string[]>>;
  selectedModules: string[];
  setSelectedModules: Dispatch<SetStateAction<string[]>>;
  dashboardMetrics: {
    assignmentsToGrade: number;
    retakeRequests: number;
    newSubmissionsToday: number;
  };
  courseData: GetCoursesByFacultyQuery;
  filteredSubmissions: UnifiedSubmission[];
  totalSubmissionsCount: number;
  totalPages: number;
  refetchUnifiedSubmissionsData: () => void;
  selectedSubmissionData: UnifiedSubmission | null;
  setSelectedSubmissionData: Dispatch<SetStateAction<UnifiedSubmission | null>>;
  updatedAssessmentGradeEntry: UpdateAssessmentSubmissionUpdatedGrade | null;
  setUpdatedAssessmentGradeEntry: Dispatch<SetStateAction<UpdateAssessmentSubmissionUpdatedGrade | null>>;
  updatedAssignmentPayload: UpdateStudentAssignmentGradeInput | null;
  setUpdatedAssignmentPayload: Dispatch<SetStateAction<UpdateStudentAssignmentGradeInput | null>>;
  updatedProjectPayload: { grade: number | null; feedback: string } | null;
  setUpdatedProjectPayload: Dispatch<SetStateAction<{ grade: number | null; feedback: string } | null>>;
  isLoading: boolean;
}

const GradingCenterSubmissionsContext = createContext<GradingCenterSubmissionsContextType>({
  selectedCourse: "",
  setSelectedCourse: () => {},
  selectedModule: "",
  setSelectedModule: () => {},
  selectedType: "All",
  setSelectedType: () => {},
  showOnlyUngraded: false,
  setShowOnlyUngraded: () => {},
  searchQuery: "",
  setSearchQuery: () => {},
  page: 1,
  setPage: () => {},
  pageSize: 10,
  setPageSize: () => {},
  sortBy: "date_desc",
  selectedCourses: [],
  setSelectedCourses: () => {},
  selectedModules: [],
  setSelectedModules: () => {},
  dashboardMetrics: {
    assignmentsToGrade: 0,
    retakeRequests: 0,
    newSubmissionsToday: 0,
  },
  courseData: {} as GetCoursesByFacultyQuery,
  filteredSubmissions: [],
  totalSubmissionsCount: 0,
  totalPages: 0,
  refetchUnifiedSubmissionsData: () => {},
  selectedSubmissionData: null,
  setSelectedSubmissionData: () => {},
  updatedAssessmentGradeEntry: null,
  setUpdatedAssessmentGradeEntry: () => {},
  updatedAssignmentPayload: null,
  setUpdatedAssignmentPayload: () => {},
  updatedProjectPayload: null,
  setUpdatedProjectPayload: () => {},
  isLoading: false,
});

const DEFAULT_PAGE_SIZE = 10;

export const GradingCenterSubmissionsProvider = ({ children }) => {
  const [selectedSubmissionData, setSelectedSubmissionData] = useState<UnifiedSubmission | null>(null);

  const [filteredSubmissions, setFilteredSubmissions] = useState<UnifiedSubmission[]>([]);

  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [showOnlyUngraded, setShowOnlyUngraded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  const [updatedAssessmentGradeEntry, setUpdatedAssessmentGradeEntry] =
    useState<UpdateAssessmentSubmissionUpdatedGrade | null>(null);
  const [updatedAssignmentPayload, setUpdatedAssignmentPayload] = useState<UpdateStudentAssignmentGradeInput | null>(
    null
  );
  const [updatedProjectPayload, setUpdatedProjectPayload] = useState<{ grade: number | null; feedback: string } | null>(
    null
  );

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: DEFAULT_PAGE_SIZE });

  const sortBy = "date_desc";

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, selectedCourse, selectedModule, selectedType, showOnlyUngraded]);

  const { data: meData } = useMeQuery();
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const { data: gradingCenterDashboardData } = useGetGradingCenterDashboardDataQuery({
    variables: {
      facultyId: meData?.me?.id || "",
    },
    skip: !meData?.me?.id,
  });

  const dashboardMetrics = useMemo(() => {
    const data = gradingCenterDashboardData?.getGradingCenterDashboardData?.data;
    return {
      assignmentsToGrade: data?.assignmentsAndAssessmentsToGrade || 0,
      retakeRequests: 0, // Not available in current data
      newSubmissionsToday: data?.newSubmissionsToday || 0,
    };
  }, [gradingCenterDashboardData]);

  const { data: courseData } = useGetCoursesByFacultyQuery({
    variables: {
      schoolId,
      userId: meData?.me?.id,
    },
    skip: !meData?.me?.id,
  });

  useEffect(() => {
    if (courseData?.getCoursesByFaculty?.data) {
      const allCourseIds = courseData.getCoursesByFaculty.data.map((course) => course.id);
      const allModuleIds = courseData.getCoursesByFaculty.data
        .map((course) => course.modules.map((module) => module.id))
        .flat();

      setSelectedCourses(allCourseIds);
      setSelectedModules(allModuleIds);
    }
  }, [courseData]);

  const {
    data: unifiedSubmissionsData,
    refetch: refetchUnifiedSubmissionsData,
    loading: unifiedSubmissionsLoading,
  } = useGetUnifiedSubmissionsByFacultyQuery({
    variables: {
      input: {
        facultyId: meData?.me?.id || "",
        searchQuery: debouncedSearchQuery,
        page,
        pageSize: pageSize,
        courseIds: selectedCourse ? [selectedCourse] : selectedCourses,
        moduleIds: selectedModule ? [selectedModule] : selectedModules,
        sortBy,
        submissionType: selectedType,
        showOnlyUngraded,
      },
    },
    skip: !meData?.me?.id,
  });

  useEffect(() => {
    refetchUnifiedSubmissionsData();
  }, [debouncedSearchQuery, page, pageSize, sortBy, selectedCourse, selectedModule, selectedType, showOnlyUngraded]);

  useEffect(() => {
    const submissionsAfterFilters =
      unifiedSubmissionsData?.getUnifiedSubmissionsByFaculty?.data?.submissions.map((submission) => ({
        ...submission,
        submittedOn: formatSubmittedDate(submission.submittedOn),
        submittedTime: formatSubmittedTime(submission.submittedTime),
      })) || [];

    setFilteredSubmissions(submissionsAfterFilters);
  }, [unifiedSubmissionsData]);

  const totalSubmissionsCount = unifiedSubmissionsData?.getUnifiedSubmissionsByFaculty?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalSubmissionsCount / pageSize);

  const value = {
    selectedCourse,
    setSelectedCourse,
    selectedModule,
    setSelectedModule,
    selectedType,
    setSelectedType,
    showOnlyUngraded,
    setShowOnlyUngraded,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortBy,
    selectedCourses,
    setSelectedCourses,
    selectedModules,
    setSelectedModules,
    dashboardMetrics,
    courseData,
    filteredSubmissions,
    totalSubmissionsCount,
    totalPages,
    refetchUnifiedSubmissionsData,
    selectedSubmissionData,
    setSelectedSubmissionData,
    updatedAssessmentGradeEntry,
    setUpdatedAssessmentGradeEntry,
    updatedAssignmentPayload,
    setUpdatedAssignmentPayload,
    updatedProjectPayload,
    setUpdatedProjectPayload,
    isLoading: unifiedSubmissionsLoading,
  };

  return <GradingCenterSubmissionsContext.Provider value={value}>{children}</GradingCenterSubmissionsContext.Provider>;
};

// make this a context
export const useGradingCenterSubmissions = () => {
  const context = useContext(GradingCenterSubmissionsContext);
  if (!context) {
    throw new Error("useGradingCenterSubmissions must be used within a GradingCenterSubmissionsProvider");
  }
  return context;
};
